import { Marker as GoogleMapsMarker } from '@react-google-maps/api'
import { observer } from 'mobx-react'
import React from 'react'
import { Marker as MapBoxMarker } from 'react-map-gl'

import {
   Button,
   colors,
   Hierarchy,
   SubVariant
} from '@flowwai-frontend/design-system'
import { MarkIcon } from '@flowwai-frontend/design-system/icons'
import { LocationValue } from '@flowwai-frontend/shared/common-components'
import { GeoLocationDetails } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { useCurrentLocation } from '../../../hooks/useCurrentLocation'
import { useMapsInstanceContext } from '../../../hooks/useMapsInstanceContext'
import { useMapsUtilityContext } from '../../../hooks/useMapsUtilityContext'
import { MapCoordinates } from '../../../types'
import { getCurrentPositionFromLocation } from '../../../utils'
import { getCoordinatesForGeoShape } from '../../../utils/GeoFieldsUtil'

import * as Styles from './styles'

interface Props {
   geoShape: LocationValue
}

const CurrentLocationTool = (props: Props): React.ReactElement => {
   const { geoShape } = props

   const {
      serviceProvidersConfig,
      mapsT,
      showCRMWarningToastWithGivenMessage
   } = useMapsUtilityContext()

   const { panTo } = useMapsInstanceContext()

   const navigateToCurrentLocation = (currentLocation: GeoLocationDetails) => {
      const hasCoordinates = getCoordinatesForGeoShape(geoShape).length > 0
      if (hasCoordinates) return

      const currentLocationMapCoordinates: MapCoordinates = getCurrentPositionFromLocation(
         currentLocation
      )
      panTo(currentLocationMapCoordinates)
   }

   const { currentLocation } = useCurrentLocation({
      onSuccess: navigateToCurrentLocation
   })

   const currentPosition: MapCoordinates | undefined = currentLocation
      ? getCurrentPositionFromLocation(currentLocation)
      : undefined

   const onClickCurrentLocation = (): void => {
      if (currentPosition) {
         panTo(currentPosition)
      } else {
         showCRMWarningToastWithGivenMessage(mapsT('allowLocationPermission'))
      }
   }

   const renderCurrentLocationMarker = (
      currentPosition: MapCoordinates
   ): React.ReactNode => {
      switch (serviceProvidersConfig.maps.serviceProvider) {
         case 'GOOGLE_MAPS': {
            return (
               <GoogleMapsMarker
                  icon={{
                     path: 0,
                     scale: 8,
                     strokeColor: colors['base-white'],
                     fillColor: colors['primary-600'],
                     fillOpacity: 1,
                     strokeWeight: 2
                  }}
                  position={currentPosition}
               />
            )
         }
         case 'MAPBOX': {
            return (
               <MapBoxMarker
                  longitude={currentPosition.lng}
                  latitude={currentPosition.lat}
               >
                  <div className={Styles.currentLocationMarkerOuterCircle}>
                     <div className={Styles.currentLocationMarkerInnerCircle} />
                  </div>
               </MapBoxMarker>
            )
         }
      }
   }

   return (
      <React.Fragment>
         <Button
            size={'Small'}
            hierarchy={Hierarchy.Tertiary}
            subVariant={SubVariant.Gray}
            className={'w-fit !rounded-xl shrink-0 hover:bg-gray-100'}
            onClick={onClickCurrentLocation}
            leftIcon={({ stroke }) => <MarkIcon stroke={stroke} />}
         >
            {mapsT('currentLocation')}
         </Button>

         {currentPosition ? renderCurrentLocationMarker(currentPosition) : null}
      </React.Fragment>
   )
}

export default observer(CurrentLocationTool)
